import { Suspense } from 'react';
import Hero from './components/Hero';
import OregonCoastWhyUs from './components/OregonCoastWhyUs';
import BuyingProcess from './components/BuyingProcess';
import { LazySection } from '../../components/base/LazySection';
import ErrorBoundary, { retryLazy } from '../../components/base/ErrorBoundary';
import { useSEOHead } from '../../hooks/useSEOHead';
import { prefetchChunk } from '../../utils/routePrefetch';
import { useEffect } from 'react';

// ── Deferred imports — retry up to 3× on chunk load failure ──────────────────
// Using retryLazy instead of bare lazy() ensures that a transient network
// hiccup or a stale chunk hash (after a new deploy) automatically retries
// before surfacing an error, eliminating false "Page failed to load" screens.
const FeaturedInventoryStrip = retryLazy(() => import('./components/FeaturedInventoryStrip'));
const ServicesTabs           = retryLazy(() => import('./components/ServicesTabs'));
const CharityCommitment      = retryLazy(() => import('./components/CharityCommitment'));
const LocalBuyerResources    = retryLazy(() => import('./components/LocalBuyerResources'));
const DealRadarCallout       = retryLazy(() => import('./components/DealRadarCallout'));
const OwnerPhilosophyStrip   = retryLazy(() => import('./components/OwnerPhilosophyStrip'));

// ── Null fallback — LazySection already renders a sized placeholder ───────────
const NullFallback = () => null;

function HomePage() {
  useSEOHead({
    title: 'Used Cars Warrenton OR | No-Fee Dealer | Rockhound',
    description: 'No-fee used car dealer in Warrenton OR. Shop cars, trucks & SUVs near Astoria & Seaside. Transparent pricing, free Oregon Coast delivery.',
    keywords: 'used cars Warrenton OR, used trucks Oregon Coast, used SUVs Astoria OR, no dealer fee used cars Oregon, used cars Seaside Oregon',
    canonicalUrl: 'https://rockhoundauto.com',
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: '46.1651;-123.9237',
  });

  // ── FAQ schema is handled by SchemaManager.tsx ────────────────────────────

  // ── Prefetch high-traffic chunks on idle ──────────────────────────────────
  useEffect(() => {
    const prefetch = () => {
      prefetchChunk(() => import('../inventory/page'));
      prefetchChunk(() => import('../financing/page'));
      prefetchChunk(() => import('../vehicle-detail/page'));
    };

    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(prefetch, { timeout: 5000 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(prefetch, 4000);
      return () => clearTimeout(id);
    }
  }, []);

  return (
    <div className="min-h-screen">
      {/* 1. Hero — eager, LCP element */}
      <Hero />

      {/* 2. Featured Inventory Strip — desktop only, deferred */}
      <div className="hidden md:block">
        <ErrorBoundary fallback={null}>
          <LazySection minHeight={260} placeholderBg="#ffffff" rootMargin="200px" label="Featured inventory strip">
            <Suspense fallback={<NullFallback />}>
              <FeaturedInventoryStrip />
            </Suspense>
          </LazySection>
        </ErrorBoundary>
      </div>

      {/* 3. Why Us — trust established early, eager */}
      <OregonCoastWhyUs />

      {/* 4. Buying Process — eager (above fold on most viewports) */}
      <BuyingProcess />

      {/* 5. Owner Philosophy — deferred, dark bg */}
      <ErrorBoundary fallback={null}>
        <LazySection minHeight={480} placeholderBg="#1A2332" rootMargin="300px" label="Owner philosophy section">
          <Suspense fallback={<NullFallback />}>
            <OwnerPhilosophyStrip />
          </Suspense>
        </LazySection>
      </ErrorBoundary>

      {/* 6. Services: Financing / Broker / Sell — deferred, dark bg */}
      <ErrorBoundary fallback={null}>
        <LazySection minHeight={600} placeholderBg="#1A2332" rootMargin="300px" label="Services section">
          <Suspense fallback={<NullFallback />}>
            <ServicesTabs />
          </Suspense>
        </LazySection>
      </ErrorBoundary>

      {/* 7. Charity Commitment + Dog Tag Military Discount — deferred */}
      <ErrorBoundary fallback={null}>
        <LazySection minHeight={520} placeholderBg="#F7F5F0" rootMargin="300px" label="Charity commitment section">
          <Suspense fallback={<NullFallback />}>
            <CharityCommitment />
          </Suspense>
        </LazySection>
      </ErrorBoundary>

      {/* 8. Local Buyer Resources — desktop only, deferred */}
      <div className="hidden md:block">
        <ErrorBoundary fallback={null}>
          <LazySection minHeight={400} placeholderBg="#ffffff" rootMargin="300px" label="Local buyer resources section">
            <Suspense fallback={<NullFallback />}>
              <LocalBuyerResources />
            </Suspense>
          </LazySection>
        </ErrorBoundary>
      </div>

      {/* 9. Deal Radar — last-chance capture before footer, deferred */}
      <ErrorBoundary fallback={null}>
        <LazySection minHeight={480} placeholderBg="#1A2332" rootMargin="300px" label="Deal radar section">
          <Suspense fallback={<NullFallback />}>
            <DealRadarCallout />
          </Suspense>
        </LazySection>
      </ErrorBoundary>
    </div>
  );
}

export default HomePage;